import {createWebHistory, createRouter} from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import {clearSession, isAuthenticated, isSessionVerified, useAuth} from "@/composables/useAuth";

const router = createRouter({
    history: createWebHistory(),
    routes,
    // Use the HTML5 history API (i.e. normal-looking routes)
    // instead of routes with hashes (e.g. example.com/#/about).
    // This may require some server configuration in production:
    // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
    mode: 'history',
    // Simulate native-like scroll behavior when navigating to a new
    // route and using back/forward buttons.
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return {top: 0, left: 0};
        }
    },
});

// API based authentication: every protected route needs a valid session on the
// backend, so the token is checked locally first and then verified with /profile.
router.beforeEach(async (routeTo, routeFrom, next) => {

    const guestOnly = routeTo.matched.some((route) => route.meta.guestOnly);

    if (guestOnly) {
        return isAuthenticated() ? next({name: 'default'}) : next();
    }

    // Everything outside the sign-in pages belongs to the panel, so routes are
    // protected unless they opt out with `meta.public`.
    const isPublic = routeTo.matched.some((route) => route.meta.public);

    if (isPublic) return next();

    if (!isAuthenticated()) {
        clearSession();

        return next({name: 'login', query: {redirectFrom: routeTo.fullPath}});
    }

    // Once the token has been verified in this page load the session is trusted;
    // an expired token is caught by the 401 handler in helpers/http.
    if (isSessionVerified()) return next();

    try {
        const {profile} = useAuth();

        await profile();

        return next();
    } catch {
        clearSession();

        return next({name: 'login', query: {redirectFrom: routeTo.fullPath}});
    }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
    // Create a `beforeResolve` hook, which fires whenever
    // `beforeRouteEnter` and `beforeRouteUpdate` would. This
    // allows us to ensure data is fetched even when params change,
    // but the resolved route does not. We put it in `meta` to
    // indicate that it's a hook we created, rather than part of
    // Vue Router (yet?).
    try {
        // For each matched route...
        for (const route of routeTo.matched) {
            await new Promise((resolve, reject) => {
                // If a `beforeResolve` hook is defined, call it with
                // the same arguments as the `beforeEnter` hook.
                if (route.meta && route.meta.beforeResolve) {
                    route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
                        // If the user chose to redirect...
                        if (args.length) {
                            // If redirecting to the same route we're coming from...
                            // Complete the redirect.
                            next(...args);
                            reject(new Error('Redirected'));
                        } else {
                            resolve();
                        }
                    });
                } else {
                    // Otherwise, continue resolving the route.
                    resolve();
                }
            });
        }
        // If a `beforeResolve` hook chose to redirect, just return.
    } catch (error) {
        return;
    }
    document.title = routeTo.meta.title + ' | ' + appConfig.title;
    // If we reach this point, continue resolving the route.
    next();
});

export default router;
